import { fetchAuthSession } from 'aws-amplify/auth';

/** Current Cognito ID token (Amplify refreshes it when needed), or null when signed out. */
export async function getIdToken(): Promise<string | null> {
  try {
    const { tokens } = await fetchAuthSession();
    return tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

let installed = false;

/**
 * Adds the Cognito ID token to every same-origin `/api/` request (`Authorization: Bearer` plus
 * `X-Auth-Token`), so the existing `fetch('/api/...')` calls are authenticated without
 * touching each call site.
 */
export function installAuthFetch(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const rawUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const target = new URL(rawUrl, window.location.origin);
    if (target.origin !== window.location.origin || !target.pathname.startsWith('/api/')) {
      return originalFetch(input, init);
    }

    const token = await getIdToken();
    if (!token) return originalFetch(input, init);

    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    // Mirror: CDNs may strip `Authorization` before it reaches the backend.
    if (!headers.has('X-Auth-Token')) headers.set('X-Auth-Token', token);
    return originalFetch(input, { ...init, headers });
  };
}
